import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Typography,
  Alert,
  Divider,
  Box,
  Chip,
  Autocomplete,
  FormHelperText,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Card,
  CardContent,
  CircularProgress,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Event,
  Settings,
  Check,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

import { ScheduleType } from '../../types';
import type {
  Schedule,
  ScheduleCreate,
  ScheduleUpdate,
  Device,
  DeviceGroup,
  Playlist,
} from '../../types';
import apiClient from '../../services/api';

interface ScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (schedule: Schedule) => void;
  schedule?: Schedule; // Для редактирования
  mode?: 'create' | 'edit' | 'duplicate';
}

// Схема валидации
const scheduleSchema = yup.object({
  name: yup
    .string()
    .required('Название обязательно')
    .min(3, 'Минимум 3 символа')
    .max(200, 'Максимум 200 символов'),
  description: yup
    .string()
    .max(1000, 'Максимум 1000 символов'),
  schedule_type: yup
    .string()
    .required('Тип расписания обязателен'),
  start_date: yup
    .date()
    .required('Дата начала обязательна')
    .min(new Date(), 'Дата начала не может быть в прошлом'),
  end_date: yup
    .date()
    .nullable()
    .min(yup.ref('start_date'), 'Дата окончания должна быть позже даты начала'),
  is_active: yup.boolean(),
  repeat_config: yup.object().when('schedule_type', {
    is: 'custom',
    then: (schema) => schema.required('Конфигурация повторения обязательна для пользовательского расписания'),
    otherwise: (schema) => schema.nullable(),
  }),
});

type ScheduleFormData = yup.InferType<typeof scheduleSchema>;

const ScheduleDialog: React.FC<ScheduleDialogProps> = ({
  open,
  onClose,
  onSave,
  schedule,
  mode = 'create',
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  
  // Данные для селекторов
  const [devices, setDevices] = useState<Device[]>([]);
  const [deviceGroups, setDeviceGroups] = useState<DeviceGroup[]>([]);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    setValue,
  } = useForm<ScheduleFormData>({
    resolver: yupResolver(scheduleSchema),
    defaultValues: {
      name: '',
      description: '',
      schedule_type: ScheduleType.ONE_TIME,
      start_date: new Date(),
      end_date: null,
      is_active: true,
      repeat_config: null,
    },
    mode: 'onChange',
  });

  const watchedScheduleType = watch('schedule_type');

  // Загружаем данные при открытии диалога
  useEffect(() => {
    if (open) {
      loadFormData();
      if (schedule && (mode === 'edit' || mode === 'duplicate')) {
        populateForm();
      } else {
        reset();
        setActiveStep(0);
      }
    }
  }, [open, schedule, mode]);

  const loadFormData = async () => {
    try {
      const [devicesData, groupsData, playlistsData] = await Promise.all([
        apiClient.getDevices(),
        // apiClient.getDeviceGroups(), // TODO: реализовать endpoint
        Promise.resolve({ items: [] as DeviceGroup[] }),
        apiClient.getPlaylists(),
      ]);

      setDevices(devicesData.items || []);
      setDeviceGroups(groupsData.items || []);
      setPlaylists(playlistsData.items || []);
    } catch (err) {
      console.error('Ошибка загрузки данных формы:', err);
      setError('Ошибка загрузки данных формы');
    }
  };

  const populateForm = () => {
    if (!schedule) return;

    const formData: Partial<ScheduleFormData> = {
      name: mode === 'duplicate' ? `${schedule.name} (копия)` : schedule.name,
      description: schedule.description,
      schedule_type: schedule.schedule_type,
      start_date: new Date(schedule.start_date),
      end_date: schedule.end_date ? new Date(schedule.end_date) : null,
      is_active: schedule.is_active,
      repeat_config: schedule.repeat_config,
    };

    Object.entries(formData).forEach(([key, value]) => {
      setValue(key as keyof ScheduleFormData, value as any);
    });
  };

  const onSubmit = async (data: ScheduleFormData) => {
    setLoading(true);
    setError(null);

    try {
      let result: Schedule;

      if (mode === 'edit' && schedule) {
        const updateData: ScheduleUpdate = {
          name: data.name,
          description: data.description || undefined,
          start_date: data.start_date.toISOString(),
          end_date: data.end_date?.toISOString() || undefined,
          is_active: data.is_active,
          repeat_config: data.repeat_config,
        };
        result = await apiClient.updateSchedule(schedule.id, updateData);
      } else {
        const createData: ScheduleCreate = {
          name: data.name,
          description: data.description || undefined,
          schedule_type: data.schedule_type as ScheduleType,
          start_date: data.start_date.toISOString(),
          end_date: data.end_date?.toISOString() || undefined,
          is_active: data.is_active,
          repeat_config: data.repeat_config,
        };
        result = await apiClient.createSchedule(createData);
      }

      onSave(result);
      handleClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Ошибка сохранения расписания');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    setActiveStep(0);
    onClose();
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const steps = [
    'Основная информация',
    'Временные настройки',
    'Конфигурация повторения',
    'Проверка и создание',
  ];

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('schedule.name')}
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    placeholder="Введите название расписания"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('common.description')}
                    fullWidth
                    multiline
                    rows={3}
                    error={!!errors.description}
                    helperText={errors.description?.message}
                    placeholder="Описание расписания (необязательно)"
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="schedule_type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.schedule_type}>
                    <InputLabel>{t('schedule.type')}</InputLabel>
                    <Select {...field} label={t('schedule.type')}>
                      <MenuItem value={ScheduleType.ONE_TIME}>
                        {t('schedule.types.oneTime')}
                      </MenuItem>
                      <MenuItem value={ScheduleType.DAILY}>
                        {t('schedule.types.daily')}
                      </MenuItem>
                      <MenuItem value={ScheduleType.WEEKLY}>
                        {t('schedule.types.weekly')}
                      </MenuItem>
                      <MenuItem value={ScheduleType.MONTHLY}>
                        {t('schedule.types.monthly')}
                      </MenuItem>
                      <MenuItem value={ScheduleType.CUSTOM}>
                        {t('schedule.types.custom')}
                      </MenuItem>
                    </Select>
                    {errors.schedule_type && (
                      <FormHelperText>{errors.schedule_type.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch {...field} checked={field.value} />}
                    label={t('schedule.isActive')}
                  />
                )}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="start_date"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      label={t('schedule.startDate')}
                      value={field.value}
                      onChange={field.onChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!errors.start_date}
                          helperText={errors.start_date?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name="end_date"
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      label={t('schedule.endDate')}
                      value={field.value}
                      onChange={field.onChange}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          error={!!errors.end_date}
                          helperText={errors.end_date?.message}
                        />
                      )}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  {t('schedule.timeHelp')}
                </Typography>
              </Grid>
            </Grid>
          </LocalizationProvider>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            {watchedScheduleType !== ScheduleType.ONE_TIME ? (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    {t('schedule.repeatConfiguration')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Конфигурация повторения будет реализована в следующей итерации
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="body1">
                        Тип повторения: <Chip label={watchedScheduleType} color="primary" />
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Typography variant="body1" color="text.secondary">
                  Разовое событие не требует настройки повторения
                </Typography>
              </Grid>
            )}
          </Grid>
        );

      case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('schedule.reviewAndCreate')}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {watch('name')}
                  </Typography>
                  {watch('description') && (
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {watch('description')}
                    </Typography>
                  )}
                  
                  <Box sx={{ mt: 2 }}>
                    <Chip 
                      label={`Тип: ${watch('schedule_type')}`} 
                      color="primary" 
                      sx={{ mr: 1, mb: 1 }} 
                    />
                    <Chip 
                      label={watch('is_active') ? 'Активно' : 'Неактивно'} 
                      color={watch('is_active') ? 'success' : 'default'}
                      sx={{ mr: 1, mb: 1 }} 
                    />
                  </Box>

                  <Typography variant="body2" sx={{ mt: 2 }}>
                    <strong>Начало:</strong> {watch('start_date')?.toLocaleString()}
                  </Typography>
                  
                  {watch('end_date') && (
                    <Typography variant="body2">
                      <strong>Окончание:</strong> {watch('end_date')?.toLocaleString()}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        );

      default:
        return null;
    }
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 0:
        return !errors.name && !errors.description && !errors.schedule_type && !!watch('name');
      case 1:
        return !errors.start_date && !errors.end_date && !!watch('start_date');
      case 2:
        return watchedScheduleType === ScheduleType.ONE_TIME || !errors.repeat_config;
      case 3:
        return isValid;
      default:
        return false;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ScheduleIcon />
        {mode === 'edit' 
          ? t('schedule.editSchedule')
          : mode === 'duplicate' 
          ? t('schedule.duplicateSchedule')
          : t('schedule.createSchedule')
        }
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel
                optional={
                  index === steps.length - 1 ? (
                    <Typography variant="caption">Последний шаг</Typography>
                  ) : null
                }
              >
                {label}
              </StepLabel>
              <StepContent>
                <Box sx={{ py: 2 }}>
                  {getStepContent(index)}
                </Box>
                <Box sx={{ mb: 2 }}>
                  <div>
                    {index === steps.length - 1 ? (
                      <Button
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                        disabled={loading || !isStepValid(index)}
                        startIcon={loading ? <CircularProgress size={20} /> : <Check />}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        {loading 
                          ? t('common.saving')
                          : mode === 'edit' 
                          ? t('common.save')
                          : t('common.create')
                        }
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={!isStepValid(index)}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        {t('common.next')}
                      </Button>
                    )}
                    <Button
                      disabled={index === 0}
                      onClick={handleBack}
                      sx={{ mt: 1, mr: 1 }}
                    >
                      {t('common.back')}
                    </Button>
                  </div>
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleDialog; 